import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getDocs,
  getCountFromServer,
  deleteDoc,
  updateDoc,
  doc,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, functions, getCallableName } from './firebase';
import { generateQueryKeywords } from '@2bforest/common/utils';
import { NewsArticle } from '../types';

const COLLECTION_NAME = 'news_articles';

export interface ListNewsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  categoryFilter?: string;
  showUncategorized?: boolean;
  searchQuery?: string;
  sortBy?: 'publishedAt' | 'sortScore';
  sortDirection?: 'asc' | 'desc';
  sourceFilter?: string;
  dateFrom?: string; // ISO string
  dateTo?: string; // ISO string
}

export interface ListNewsResult {
  articles: NewsArticle[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * List news articles with database-level filtering and pagination
 */
export async function listNews({
  pageSize = 50,
  lastDoc = null,
  categoryFilter,
  showUncategorized = false,
  searchQuery,
  sortBy = 'sortScore',
  sortDirection = 'desc',
  sourceFilter,
  dateFrom,
  dateTo,
}: ListNewsParams = {}): Promise<ListNewsResult> {
  try {
    // Base collection reference
    const collectionRef = collection(getDb(), COLLECTION_NAME);

    // Build query based on filters
    let q;

    // Search query using searchKeywords (database-level)
    if (searchQuery && searchQuery.trim().length > 0) {
      const searchKeywords = generateQueryKeywords(searchQuery);

      if (searchKeywords.length > 0) {
        // When searching, we must use searchKeywords filter
        if (showUncategorized) {
          q = query(
            collectionRef,
            where('searchKeywords', 'array-contains-any', searchKeywords),
            where('categoryIds', '==', null),
            orderBy(sortBy, sortDirection)
          );
        } else if (categoryFilter) {
          q = query(
            collectionRef,
            where('searchKeywords', 'array-contains-any', searchKeywords),
            where('categoryIds', 'array-contains', categoryFilter),
            orderBy(sortBy, sortDirection)
          );
        } else {
          q = query(
            collectionRef,
            where('searchKeywords', 'array-contains-any', searchKeywords),
            orderBy(sortBy, sortDirection)
          );
        }
      } else {
        // Empty search query after filtering, apply normal filters
        if (showUncategorized) {
          q = query(
            collectionRef,
            where('categoryIds', '==', null),
            orderBy(sortBy, sortDirection)
          );
        } else if (categoryFilter) {
          q = query(
            collectionRef,
            where('categoryIds', 'array-contains', categoryFilter),
            orderBy(sortBy, sortDirection)
          );
        } else {
          q = query(collectionRef, orderBy(sortBy, sortDirection));
        }
      }
    } else {
      // No search query, apply normal filters
      if (showUncategorized) {
        q = query(collectionRef, where('categoryIds', '==', null), orderBy(sortBy, sortDirection));
      } else if (categoryFilter) {
        q = query(
          collectionRef,
          where('categoryIds', 'array-contains', categoryFilter),
          orderBy(sortBy, sortDirection)
        );
      } else {
        q = query(collectionRef, orderBy(sortBy, sortDirection));
      }
    }

    // Add pagination
    q = query(q, limit(pageSize + 1));

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const querySnapshot = await getDocs(q);
    const docs = querySnapshot.docs;

    // Check if there are more results
    const hasMore = docs.length > pageSize;
    const articleDocs = hasMore ? docs.slice(0, pageSize) : docs;

    let articles = articleDocs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        // Convert Firestore Timestamp to ISO string for consistency
        publishedAt: data.publishedAt?.toDate
          ? data.publishedAt.toDate().toISOString()
          : data.publishedAt,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt,
      };
    }) as NewsArticle[];

    // Apply source filter (client-side for now, can be moved to query if needed)
    if (sourceFilter) {
      articles = articles.filter(article => article.source === sourceFilter);
    }

    // Apply date range filter (client-side for now)
    if (dateFrom || dateTo) {
      articles = articles.filter(article => {
        const articleDate = new Date(article.publishedAt);
        if (dateFrom && articleDate < new Date(dateFrom)) return false;
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // Include entire end date
          if (articleDate > toDate) return false;
        }
        return true;
      });
    }

    return {
      articles,
      lastDoc: articleDocs.length > 0 ? articleDocs[articleDocs.length - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error('Error listing news articles:', error);
    throw error;
  }
}

/**
 * Update a news article (e.g., update categoryIds)
 */
export async function updateNews(
  articleId: string,
  updates: Partial<Omit<NewsArticle, 'id'>>
): Promise<void> {
  try {
    await updateDoc(doc(getDb(), COLLECTION_NAME, articleId), updates);
  } catch (error) {
    console.error('Error updating news article:', error);
    throw error;
  }
}

/**
 * Delete a news article
 */
export async function deleteNews(articleId: string): Promise<void> {
  try {
    await deleteDoc(doc(getDb(), COLLECTION_NAME, articleId));
  } catch (error) {
    console.error('Error deleting news article:', error);
    throw error;
  }
}

/**
 * Get all unique sources from news articles
 */
export async function getNewsSources(): Promise<string[]> {
  try {
    const q = query(collection(getDb(), COLLECTION_NAME), orderBy('source', 'asc'));
    const snapshot = await getDocs(q);

    const sources = new Set<string>();
    snapshot.docs.forEach(doc => {
      const article = doc.data();
      if (article.source) {
        sources.add(article.source);
      }
    });

    return Array.from(sources).sort();
  } catch (error) {
    console.error('Error getting news sources:', error);
    throw error;
  }
}

/**
 * Get news statistics
 */
export async function getNewsStats(): Promise<{
  total: number;
  today: number;
  thisWeek: number;
}> {
  try {
    // Get all articles (or use query with limits for large datasets)
    const q = query(collection(getDb(), COLLECTION_NAME), orderBy('publishedAt', 'desc'));
    const snapshot = await getDocs(q);

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    let todayCount = 0;
    let weekCount = 0;

    snapshot.docs.forEach(doc => {
      const article = doc.data();
      const publishedAt = article.publishedAt?.toDate
        ? article.publishedAt.toDate()
        : new Date(article.publishedAt);

      if (publishedAt >= todayStart) {
        todayCount++;
      }
      if (publishedAt >= weekStart) {
        weekCount++;
      }
    });

    return {
      total: snapshot.size,
      today: todayCount,
      thisWeek: weekCount,
    };
  } catch (error) {
    console.error('Error getting news stats:', error);
    throw error;
  }
}

/**
 * Trigger category reassignment for all articles or specific articles
 */
export async function reassignCategories(articleIds?: string[]): Promise<{
  success: boolean;
  message: string;
  processed: number;
  updated: number;
}> {
  try {
    const reassignFn = httpsCallable(functions, getCallableName('reassignCategories'));
    const result = await reassignFn({ articleIds });
    return result.data as any;
  } catch (error) {
    console.error('Error reassigning categories:', error);
    throw error;
  }
}

export async function countArticles(): Promise<number> {
  const snap = await getCountFromServer(collection(getDb(), COLLECTION_NAME));
  return snap.data().count;
}

export async function countArticlesByCategory(categoryId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(getDb(), COLLECTION_NAME), where('categoryIds', 'array-contains', categoryId))
  );
  return snap.data().count;
}
